/* eslint-disable no-param-reassign */

import { createAsyncThunk, createSlice, PayloadAction, SerializedError } from '@reduxjs/toolkit';

import { GetMe, TermIsAccepted } from '../../api/openapi';
import { accountApi } from '../../api';
import { RootState } from '../rootReducer';

interface State {
  loggedIn: null | boolean;
  currentUser: GetMe | null;
  currentUserInitials: string | null;
  currentUserLoading: 'idle' | 'pending';
  currentUserError: SerializedError | null;

  termIsAccepted: TermIsAccepted | null;
  termIsAcceptedLoading: 'idle' | 'pending';
  termIsAcceptedError: SerializedError | null;
  termIsAcceptedValue: boolean | null;
}

const initialState: State = {
  loggedIn: null,
  currentUser: null,
  currentUserInitials: null,
  currentUserLoading: 'idle',
  currentUserError: null,

  termIsAccepted: null,
  termIsAcceptedLoading: 'idle',
  termIsAcceptedError: null,
  termIsAcceptedValue: false,
};

const getInitials = (str: string): string => {
  const names = str.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }

  return initials;
};

export const fetchGetMe = createAsyncThunk<
  GetMe,
  void,
  {
    state: RootState;
  }
>('auth/fetchGetMe', async () => {
  const response = await accountApi.getMe({
    requireAuth: false,
  });
  return response.data;
});

export const fetchTermIsAccepted = createAsyncThunk<
  TermIsAccepted,
  void,
  {
    state: RootState;
  }
>('auth/fetchTermIsAccepted', async () => {
  const response = await accountApi.termIsAccepted({
    uuid: 'latest',
  });
  return response.data;
});

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    termIsAcceptedValueUpdated(state, action: PayloadAction<boolean>) {
      state.termIsAcceptedValue = action.payload;
    },
  },
  extraReducers: (builder) => {
    /*
     * fetchMyAccount
     */
    builder.addCase(fetchGetMe.pending, (state, action) => {
      state.currentUserLoading = 'pending';
    });
    builder.addCase(fetchGetMe.fulfilled, (state, action) => {
      state.currentUserLoading = 'idle';
      state.currentUser = action.payload;
      state.currentUserInitials = getInitials(action.payload.name);

      state.loggedIn = true;
    });
    builder.addCase(fetchGetMe.rejected, (state, action) => {
      state.currentUserLoading = 'idle';
      state.currentUserError = action.error;
      state.loggedIn = false;
    });
    /*
     * fetchTermIsAccepted
     */
    builder.addCase(fetchTermIsAccepted.pending, (state, action) => {
      state.termIsAcceptedLoading = 'pending';
    });
    builder.addCase(fetchTermIsAccepted.fulfilled, (state, action) => {
      state.termIsAcceptedLoading = 'idle';
      state.termIsAccepted = action.payload;
      state.termIsAcceptedValue = !!action.payload.acceptedAt;
    });
    builder.addCase(fetchTermIsAccepted.rejected, (state, action) => {
      state.termIsAcceptedLoading = 'idle';
      state.termIsAcceptedError = action.error;
      state.termIsAcceptedValue = false;
    });
  },
});

export const { termIsAcceptedValueUpdated } = slice.actions;

export default slice.reducer;
